import React, { useContext, useRef, useState } from 'react';
import { Switch, Route } from 'react-router-dom';
import { SiteHeader } from './HubLayout/components/PageLayout/SiteHeader';
import { PageNav } from './HubLayout/components/PageLayout/PageNav';
import { HomePage } from './HubLayout/components/HomePage';
import { FeaturesHomePage } from './AGSS/components/Features/FeaturesHomePage';
import { ArtifactSearchResults } from './AGSS/components/Artifact/ArtifactSearchResultsPage';
import { CapabilityContext } from './Common/components/Capabilities/CapabilityContext';
import { Capability } from './Common/components/Capabilities/capability';
import { getThemeFromString } from './Common/util/localStorageUtils';
import { ThemeContext } from './HubLayout/models/ThemeContext';
import { ReleaseLatestListPage } from './AGSS/components/MultistageReleaseData/ReleaseLatestListPage';
import { ReleaseDetailsPage } from './AGSS/components/MultistageReleaseData/ReleaseDetailsPage';
import { BuildInstanceDetailsPage } from './AGSS/components/Build/BuildInstanceDetailsPage';
import { ArtifactDetailsPage } from './AGSS/components/Artifact/ArtifactDetailsPage';
import { Administration } from './Administration/components/Administration';
import { GeneralMessageBar } from './HubLayout/components/MessageBars/GeneralMessageBar';
import { ServiceTreeMessageBar } from './HubLayout/components/MessageBars/ServiceTreeMessageBar';
import { NonAdminModeBanner } from './HubLayout/components/MessageBars/NonAdminModeBanner';
import { useIsMobile } from './Common/hooks/useIsMobile';
import { MobilePageNav } from './HubLayout/components/PageLayout/Mobile/MobilePageNav';
import { BuildListPage } from './AGSS/components/MultistageReleaseData/BuildListPage';
import { CloudStatus } from './SRSDS/components/CloudStatus';
import { KustoAccessPage } from './ABSM/components/KustoAccess/KustoAccessPage';
import { KustoAdminPage } from './ABSM/components/KustoAccess/KustoAdminPage';
import { isKustoAccessConfiguredForCloud } from './ABSM/components/KustoAccess/kustoAccessConfig';
import { EnvironmentDataPage } from './AGCP/components/EnvironmentData';
import { DRITools } from './Administration/components/DRITools/DRITools';
import { AssistantChat } from './Administration/components/AssistantChat/AssistantChat';

export const HubApp: React.FC = () => {
    const [scrollTop, setScrollTop] = useState<number>(0);

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        setScrollTop(event.currentTarget.scrollTop);
    };

    // Use capabilities context
    const capabilities = useContext(CapabilityContext);
    // Only show metrics report and latest egress date in public cloud
    const inPublicCloud = capabilities.check(Capability.public);
    // Only show the service-oriented pages if user has Beta UI role and we are ready to show them
    const showServiceOrientedPages = false; //capabilities.check(Capability.betaUIFeaturesRole);
    const showAdmin = capabilities.check(Capability.srmAdminRole);
    // Kusto access is still being validated in the sovereign and air-gapped clouds, so the
    // customer page is admin-only there, and only once that cloud has a cluster configured.
    const showKustoAccess =
        inPublicCloud || (showAdmin && isKustoAccessConfiguredForCloud());

    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);
    const isMobile = useIsMobile();
    const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
    const showPanel = () => setIsPanelOpen(true);
    const hidePanel = () => setIsPanelOpen(false);

    const scrollBarRef = useRef<HTMLDivElement>(null);

    return (
        <div
            className="HubApp-root"
            style={{ color: theme.semanticColors.bodyText }}
        >
            {/* Skip link: first focusable element, allows keyboard users to bypass the site header and primary nav. */}
            <a
                href="#content"
                className="HubApp-skipLink"
                style={{
                    position: 'absolute',
                    left: '-10000px',
                    top: 'auto',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden',
                    zIndex: 10000,
                }}
                onFocus={(e) => {
                    const el = e.currentTarget;
                    el.style.left = '8px';
                    el.style.top = '8px';
                    el.style.width = 'auto';
                    el.style.height = 'auto';
                    el.style.padding = '8px 12px';
                    el.style.backgroundColor = theme.palette.white;
                    el.style.color = theme.semanticColors.bodyText;
                    el.style.border = `2px solid ${theme.palette.themePrimary}`;
                }}
                onBlur={(e) => {
                    const el = e.currentTarget;
                    el.style.left = '-10000px';
                    el.style.top = 'auto';
                    el.style.width = '1px';
                    el.style.height = '1px';
                    el.style.padding = '0';
                    el.style.border = 'none';
                }}
            >
                Skip to main content
            </a>

            <SiteHeader
                showSearch={capabilities.check(
                    Capability.artifactRereplicateRole
                )}
                openMobileNav={showPanel}
            />

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flex: 1,
                    alignItems: 'stretch',
                    width: '100%',
                    height: 'calc(100% - 60px)',
                }}
            >
                <nav aria-label="Primary" style={{ display: 'flex' }}>
                    {!isMobile && <PageNav />}
                    {isMobile && (
                        <MobilePageNav
                            isOpen={isPanelOpen}
                            dismissPanel={hidePanel}
                        />
                    )}
                </nav>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        flex: 1,
                        height: '100%',
                        width: '0px',
                    }}
                >
                    {<NonAdminModeBanner />}
                    {<GeneralMessageBar />}
                    {<ServiceTreeMessageBar />}
                    <main
                        id="content"
                        ref={scrollBarRef}
                        className="HubApp-content"
                        tabIndex={-1}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            alignItems: 'stretch',
                            backgroundColor: theme.surfaces.appBackground,
                            padding: isMobile ? '0px' : undefined,
                        }}
                        onScroll={handleScroll}
                    >
                        <Switch>
                            <Route path="/" exact component={HomePage} />
                            {/* Multi-stage Release Pages */}
                            <Route
                                path="/BuildStatus/Artifact/Search/:artifactId?"
                                component={ArtifactSearchResults}
                            />
                            <Route
                                path="/BuildStatus/Artifact/:artifactId"
                                component={ArtifactDetailsPage}
                            />
                            <Route
                                path="/ReleaseStatus/Release/:releaseCorrelationId"
                                component={ReleaseDetailsPage}
                            />
                            <Route
                                path="/ReleaseStatus/Latest/:count?"
                                render={(props) => (
                                    <ReleaseLatestListPage
                                        {...props}
                                        scrollBarRef={scrollBarRef}
                                        scrollTop={scrollTop}
                                    />
                                )}
                            />
                            {/* Build Pages */}
                            <Route
                                path="/BuildStatus/Build/:buildInstanceCorrelationId"
                                component={BuildInstanceDetailsPage}
                            />
                            <Route
                                path="/BuildStatus"
                                component={BuildListPage}
                            />
                            {showKustoAccess && (
                                <Route
                                    path="/Kusto"
                                    component={KustoAccessPage}
                                />
                            )}
                            {showServiceOrientedPages && (
                                <Route
                                    path="/Features"
                                    exact
                                    component={FeaturesHomePage}
                                />
                            )}
                            {showAdmin && (
                                <Route
                                    path="/Administration"
                                    component={Administration}
                                />
                            )}
                            {showAdmin && (
                                <Route
                                    path="/CloudStatus"
                                    component={CloudStatus}
                                />
                            )}
                            {showAdmin && (
                                <Route
                                    path="/EnvironmentData"
                                    component={EnvironmentDataPage}
                                />
                            )}
                            {showAdmin && (
                                <Route path="/DRITools" component={DRITools} />
                            )}
                            {showAdmin && (
                                <Route
                                    path="/KustoAdmin"
                                    component={KustoAdminPage}
                                />
                            )}
                            {showAdmin && (
                                <Route
                                    path="/AssistantChat"
                                    component={AssistantChat}
                                />
                            )}
                        </Switch>
                    </main>
                </div>
            </div>
        </div>
    );
};
